import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { Grid, Button, Paper, Table, TableHead, TableBody, TableRow, TableCell, IconButton, Chip, Typography, Box } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import i18next from 'i18next';
import moment from 'moment';
import { dispatchException, dispatchMessage } from 'helper/snackbar';
import { LIST_HOTEL_CONTRACTS_QUERY, DELETE_CONTRACT_MUTATION } from './gql';
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog';

interface ContractsTabProps {
  hotelId: number;
  canEdit: boolean;
}

const ContractsTab: React.FC<ContractsTabProps> = ({ hotelId, canEdit }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // no local create form state here; creation happens in ContractDetail view

  const { data, loading, refetch } = useQuery(LIST_HOTEL_CONTRACTS_QUERY, {
    variables: { hotelId },
  });

  // removed: creation handled in ContractDetail

  const [deleteContract] = useMutation(DELETE_CONTRACT_MUTATION, {
    onCompleted: () => {
      dispatchMessage(dispatch, String(i18next.t('contracts-delete-success')));
      refetch();
    },
    onError: (error) => {
      dispatchException(dispatch, error);
    },
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const handleOpenCreate = () => {
    navigate(`/contracts/new?hotelId=${hotelId}`);
  };

  const handleOpenDelete = (id: number) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };
  const handleConfirmDelete = () => {
    if (deleteId) {
      deleteContract({ variables: { id: deleteId } });
    }
    setDeleteDialogOpen(false);
    setDeleteId(null);
  };

  const formatAmount = (cents: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(cents / 100);
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">{i18next.t('contracts-title')}</Typography>
          {canEdit && (
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleOpenCreate}>
              {i18next.t('contracts-create-new')}
            </Button>
          )}
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{i18next.t('contracts-header-type')}</TableCell>
                <TableCell>{i18next.t('contracts-header-validity')}</TableCell>
                <TableCell>{i18next.t('contracts-header-interval')}</TableCell>
                <TableCell align="right">{i18next.t('contracts-header-amount')}</TableCell>
                <TableCell>{i18next.t('contracts-header-status')}</TableCell>
                <TableCell>{i18next.t('contracts-header-next-invoice')}</TableCell>
                <TableCell>{i18next.t('contracts-header-created-at')}</TableCell>
                <TableCell align="center">{i18next.t('contracts-header-actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.hotelContracts?.map((contract: any) => (
                <TableRow key={contract.id}>
                  <TableCell>{String(i18next.t(`contracts-type-${contract.type}`, { defaultValue: contract.type }))}</TableCell>
                  <TableCell>
                    {moment(contract.validFrom).format('DD.MM.YYYY')} - {moment(contract.validTo).format('DD.MM.YYYY')}
                  </TableCell>
                  <TableCell>
                    {String(
                      i18next.t(`contracts-interval-${contract.invoiceInterval}`, { defaultValue: contract.invoiceInterval }),
                    )}
                  </TableCell>
                  <TableCell align="right">{formatAmount(contract.amountCents)}</TableCell>
                  <TableCell>
                    {contract.isActive ? (
                      <Chip label={String(i18next.t('active'))} color="success" size="small" />
                    ) : (
                      <Chip label={String(i18next.t('inactive'))} color="default" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    {contract.nextInvoiceDate ? moment(contract.nextInvoiceDate).format('DD.MM.YYYY') : '-'}
                  </TableCell>
                  <TableCell>{moment(contract.createdAt).format('DD.MM.YYYY')}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/contracts/${contract.id}/edit`)}
                      disabled={!canEdit}
                      title={String(i18next.t('edit'))}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDelete(contract.id)}
                      disabled={!canEdit}
                      title={String(i18next.t('delete'))}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {(!data?.hotelContracts || data.hotelContracts.length === 0) && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="textSecondary">
                      {i18next.t('contracts-empty')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </Grid>

      {/* Create handled in ContractDetail view */}
      <ConfirmationDialog
        title={String(i18next.t('contracts-delete-title'))}
        open={deleteDialogOpen}
        setOpen={setDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
      >
        {i18next.t('contracts-delete-confirm')}
      </ConfirmationDialog>
    </Grid>
  );
};

export default ContractsTab;
